import React from 'react';
import {
  Grid,
  Typography,
  CssBaseline,
  Container,
  Box,
  Card,
  Divider,
} from '@mui/material';
import { Link } from 'react-router-dom';

import styled from 'styled-components';
import { Navbar } from '../Navbar/Navbar';
import { Footer } from '../Footer';
import { Colors } from '../style/theme';
import SEO from '../../components/SEO';

const FormSide = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TopMargin = styled.div`
  margin-top: 100px;
`;

const StyledCard = styled.div`
  background-color: #f7f7f7;
  padding: 0px;
 box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
`;

const blogData = [
  {
    title: 'Why Every Business Needs Monitoring — Now More Than Ever',
    description:
      "Whether you're managing a fast-growing startup, an e-commerce store, or a SaaS platform — staying online isn’t optional anymore. In today’s digital world, your website or app is more than just a tool; it’s your storefront...",
    author: 'Mahi Choubey',
    date: 'August 1, 2025',
    id: 'blog-1'
  },
  {
    title: 'What Happens When You Don’t Monitor Your Nodes & Domains?',
    description:
      "Imagine waking up to find your website has been down for hours. Leads are lost, customers are complaining, and your domain? Expired — without warning. Your support inbox is full, your sales team is panicking...",
    author: 'Raja Jaimini',
    date: 'August 1, 2025',
    id: 'blog-2'
  },
  {
    title:
      ' Monitor More, Spend Less- Why WatchNod Is the Smartest Budget-Friendly Choice',
    description:
      "Most monitoring tools are built for big enterprises — and priced that way. But what about startups, indie developers, digital agencies, or SaaS founders who need reliable monitoring without blowing the budget...",
    author: 'Mahi Choubey',
    date: 'August 1, 2025',
    id: 'blog-3'
  },
  {
    title:
      "How Domain Monitoring Prevents Security & SEO Nightmares",
    description:
      "Your domain is more than just a web address — it’s your brand’s digital identity. It’s how customers find you, trust you, and interact with your services. But what happens if your domain expires, gets hijacked...",
    author: 'Raja Jaimini',
    date: 'August 1, 2025',
    id: 'blog-4'
  },
];

const titleToSlug = (title) => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

function BlogPage() {
  const leftBlogs = blogData.filter((_, i) => i % 2 === 0);
  const rightBlogs = blogData.filter((_, i) => i % 2 !== 0);

  return (
    <>
      <SEO
        title="WatchNod Blog | Server, API & Uptime Monitoring Guides"
        description="Explore WatchNod blog for guides on server monitoring, API monitoring, uptime tracking, and tips to improve performance and reliability."
        url="/blog"
        type="website"
      />
      <Navbar />
      <TopMargin />
      <CssBaseline />
      <StyledCard>
        <Card>
          <FormSide>
            <Container maxWidth="xl" sx={{ py: 6 }}>
              <Typography
                variant="h3"
                gutterBottom
                textAlign='center'
              >
                Smart Reads for Curious Minds – Ideas, Insights & Innovation
              </Typography>

              <Grid container spacing={0} alignItems="stretch" my={6}>
                {/* Left column */}
                <Grid item xs={12} md={5}>
                  {leftBlogs.map((blog, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 6,
                        minHeight: "200px",
                        pr: { md: 4 },
                        pb: 4,
                      }}
                    >
                      <Typography
                        variant="title"
                        sx={{ color: Colors.primary, fontWeight: 700 }}
                      >
                        {blog.category}
                      </Typography>

                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mt: 5,
                          mb: 1,
                        }}
                      >
                        {blog.title}
                      </Typography>

                      <Typography color="text.secondary">
                        {blog.description}
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 2,
                        }}
                      >
                        {/* Author & Date */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                            color="text.primary"
                          >
                            {blog.author}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {blog.date}
                          </Typography>
                        </Box>

                        {/* Read Button */}
                        <Box>
                          <Typography
                            variant="button"
                            component={Link}
                            to={`/blog/${titleToSlug(blog.title)}`}
                            sx={{
                              textDecoration: 'none',
                              color: Colors.primary,
                              fontWeight: 700,
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Read
                          </Typography>
                        </Box>
                      </Box>

                    </Box>
                  ))}
                </Grid>

                {/* Vertical Divider */}
                <Grid
                  item
                  md={2}
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'center',
                  }}
                >
                  <Divider
                    orientation="vertical"
                    sx={{
                      height: '100%',
                      borderColor: '#d1d5db',
                      borderRightWidth: 2,
                    }}
                  />
                </Grid>

                {/* Right column */}
                <Grid item xs={12} md={5}>
                  {rightBlogs.map((blog, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 6,
                        minHeight: "200px",
                        pr: { md: 4 },
                        pb: 4,
                      }}
                    >
                      <Typography
                        variant="title"
                        sx={{ color: Colors.primary, fontWeight: 700 }}
                      >
                        {blog.category}
                      </Typography>

                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mt: 5,
                          mb: 1,
                        }}
                      >
                        {blog.title}
                      </Typography>

                      <Typography color="text.secondary">
                        {blog.description}
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 2,
                        }}
                      >
                        {/* Author & Date */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                            color="text.primary"
                          >
                            {blog.author}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {blog.date}
                          </Typography>
                        </Box>

                        {/* Read Button */}
                        <Box>
                          <Typography
                            variant="button"
                            component={Link}
                            to={`/blog/${titleToSlug(blog.title)}`}
                            sx={{
                              textDecoration: 'none',
                              color: Colors.primary,
                              fontWeight: 700,
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Read
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </Container>

          </FormSide>
        </Card>
      </StyledCard >
      <Box sx={{ position: 'relative', bottom: 0, width: '100%' }}>
        <Footer />
      </Box>
    </>
  );
}

export default BlogPage;
